var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { AuthorizationStatus, getMessaging, } from '@react-native-firebase/messaging';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { keepPreviousData, useQuery, useQueryClient, } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BackHandler, Dimensions, Image, Linking, PermissionsAndroid, Platform, ScrollView, } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DeviceInfo from 'react-native-device-info';
import styled from 'styled-components/native';
import CrusherClubLogo from '@/assets/icon/logo.svg';
import { accessTokenAtom, featureFlagAtom, isAnonymousUserAtom, useMe, } from '@/atoms/Auth';
import { currentLocationAtom } from '@/atoms/Location';
import { dismissedHomePopupIdsAtom, hasShownTutorialIntroPopupAtom, tutorialIntroPopupShownUserIdAtom, } from '@/atoms/User';
import { ScreenLayout } from '@/components/ScreenLayout';
import { prefetchRemoteImage } from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { isFeatureEnabled } from '@/features/sandboxFeatures';
import { GetClientVersionStatusResponseDtoStatusEnum } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { nearbyAccessibilityStatusQueryFn, nearbyAccessibilityStatusQueryKey, } from '@/hooks/useNearbyAccessibilityStatus';
import { useIsForeground } from '@/hooks/useIsForeground';
import { useUserTutorialProgress } from '@/hooks/useUserTutorialProgress';
import Logger from '@/logging/Logger';
import AppUpgradeNeededBottomSheet from '@/modals/AppUpgradeNeededBottomSheet';
import MarketingPushAgreementBottomSheet from '@/modals/MarketingPushAgreementBottomSheet';
import GeolocationPermissionBottomSheet from '@/modals/GeolocationPermissionBottomSheet';
import { allMainMissionsCompletedIn } from '@/screens/TutorialMissionScreen/constants';
import { openAppDeepLink } from '@/utils/appLinkNavigation';
import { isAppDeepLink } from '@/utils/deepLinkUtils';
import GeolocationUtils from '@/utils/GeolocationUtils';
import ToastUtils from '@/utils/ToastUtils';
import { getDeferredDeepLinkUrl } from '@/deeplink/DeferredDeepLink';
import ArticleSection from './sections/ArticleSection';
import CategoryChipSection from './sections/CategoryChipSection';
import FooterButtonsSection from './sections/FooterButtonsSection';
import MainBannerSection from './sections/MainBannerSection';
import QuickMenuSection from './sections/QuickMenuSection';
import RecommendedContentSection from './sections/RecommendedContentSection';
import SearchButtonSection from './sections/SearchButtonSection';
import StripBannerSection from './sections/StripBannerSection';
import HomePopupModal from './components/HomePopupModal';
import TutorialIntroPopup from './components/TutorialIntroPopup';
import { hasSeenTutorialIntroPopup } from './tutorialIntroPopupSeen';
const SCREEN_WIDTH = Dimensions.get('window').width;
const BG_ASPECT_RATIO = 1170 / 987; // original image aspect ratio
const BG_HEIGHT = SCREEN_WIDTH / BG_ASPECT_RATIO;
const HomeScreenV2 = ({ navigation }) => {
    var _a, _b, _c, _d, _e;
    const { api } = useAppComponents();
    const insets = useSafeAreaInsets();
    const accessToken = useAtomValue(accessTokenAtom);
    const [currentLocation, setCurrentLocation] = useAtom(currentLocationAtom);
    const [geolocationErrorReason, setGeolocationErrorReason] = useState(null);
    const [showAppUpgradeNeeded, setShowAppUpgradeNeeded] = useState(true);
    const _isForeground = useIsForeground();
    const { userInfo, syncUserInfo } = useMe();
    // 홈 데이터 조회에 쓸 위치는 **처음 확보한 좌표 하나로 고정**한다.
    // currentLocationAtom 은 ItemMapView 의 watchPosition(enableHighAccuracy, 5s)이 계속
    // 갱신하므로, 원시 좌표를 쿼리 키에 넣으면 GPS 가 흔들릴 때마다 키가 바뀌어 홈 API 를
    // 무한 재호출한다(실측: 발열). quickAction 의 hasUnconqueredPlaceNearby 는 "진입 시점에
    // 반경 500m 안에 미정복 매장이 있나" 판정이라 좌표를 실시간으로 따라갈 필요가 없다.
    const [homeQueryLocation, setHomeQueryLocation] = useState(null);
    useEffect(() => {
        if (currentLocation && !homeQueryLocation) {
            setHomeQueryLocation(currentLocation);
        }
    }, [currentLocation, homeQueryLocation]);
    // Fetch all home screen data in a single API call.
    // 위치 획득 전(위치 없이 1차 호출) → 후(고정 좌표로 1회 재조회) 두 번만 돈다.
    // quickAction.challenge(퀵메뉴 카드+툴팁)는 이 응답 하나에서 나온다 — 별도 쿼리를 만들지 않는다.
    const { data: homeData, isLoading: isHomeDataLoading } = useQuery({
        queryKey: [
            'HomeScreenData',
            homeQueryLocation
                ? `${homeQueryLocation.latitude},${homeQueryLocation.longitude}`
                : null,
        ],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const result = (yield api.getHomeScreenData(homeQueryLocation
                ? {
                    currentLocation: {
                        lat: homeQueryLocation.latitude,
                        lng: homeQueryLocation.longitude,
                    },
                }
                : undefined)).data;
            return result;
        }),
        placeholderData: keepPreviousData,
        // 포커스 복귀·리렌더로 재호출되지 않게 한다 (홈은 진입당 1~2회면 충분).
        staleTime: 5 * 60 * 1000,
    });
    const { data: versionData } = useQuery({
        queryKey: ['ClientVersionStatus'],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const result = (yield api.getClientVersionStatusPost({
                version: DeviceInfo.getVersion(),
            })).data;
            return result;
        }),
    });
    const versionStatusMessage = versionData === null || versionData === void 0 ? void 0 : versionData.message;
    const versionStatus = versionData === null || versionData === void 0 ? void 0 : versionData.status;
    const isAnonymousUser = useAtomValue(isAnonymousUserAtom);
    const featureFlags = useAtomValue(featureFlagAtom);
    // 인트로 팝업 노출 여부는 **유저 단위**로 기록한다. 기기 단위 boolean 이면 같은 기기에서
    // 계정을 바꿨을 때(재가입/계정 전환) 새 유저가 팝업을 영영 못 본다.
    const [tutorialIntroPopupShownUserId, setTutorialIntroPopupShownUserId] = useAtom(tutorialIntroPopupShownUserIdAtom);
    const [legacyHasShownTutorialIntroPopup, setLegacyHasShownTutorialIntroPopup,] = useAtom(hasShownTutorialIntroPopupAtom);
    const myUserId = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.id) !== null && _a !== void 0 ? _a : null;
    // 구버전(기기 단위 boolean)에서 올라온 기기 이관: 이미 본 것으로 기록돼 있으면 지금 로그인한
    // 유저가 본 것으로 1회 귀속시킨다. 이게 없으면 업데이트 직후 전 유저에게 전면 팝업이 다시 뜬다.
    useEffect(() => {
        if (myUserId == null)
            return;
        if (tutorialIntroPopupShownUserId != null)
            return;
        if (!legacyHasShownTutorialIntroPopup)
            return;
        setTutorialIntroPopupShownUserId(myUserId);
    }, [
        myUserId,
        tutorialIntroPopupShownUserId,
        legacyHasShownTutorialIntroPopup,
        setTutorialIntroPopupShownUserId,
    ]);
    const hasShownTutorialIntroPopup = hasSeenTutorialIntroPopup({
        userId: myUserId,
        shownUserId: tutorialIntroPopupShownUserId,
        legacyShown: legacyHasShownTutorialIntroPopup,
    });
    const setHasShownTutorialIntroPopup = useCallback(() => {
        if (myUserId != null) {
            setTutorialIntroPopupShownUserId(myUserId);
        }
        setLegacyHasShownTutorialIntroPopup(true);
    }, [
        myUserId,
        setTutorialIntroPopupShownUserId,
        setLegacyHasShownTutorialIntroPopup,
    ]);
    // 윌리의 외출 NUX 튜토리얼 외출 유도 전면 팝업: 가입자 + 미노출 1회만 (1초 딜레이).
    // __DEV__: Figma 시각 검증용 강제 활성화 (사용 후 false로 되돌릴 것)
    const __DEV_FORCE_INTRO_POPUP__ = false;
    const [tutorialIntroPopupReady, setTutorialIntroPopupReady] = useState(false);
    // 서버 progress 로 메인 미션 완료 여부 확인. 앱 삭제/재설치로 AsyncStorage
    // 기반 atom 이 리셋되어도 서버 진행 상태로 "이미 완료자" 인지 판단해 팝업 재노출을 막는다.
    const { data: tutorialProgress } = useUserTutorialProgress();
    const allMainTutorialMissionsCompleted = useMemo(() => {
        if (!tutorialProgress) {
            return undefined;
        }
        // 미션 구성은 가입 시점에 따라 다르므로(v1/v2) 서버 응답에서 파생한다. 하드코딩하면
        // v2 유저가 존재하지도 않는 미션(UPVOTE_ACCESSIBILITY) 완료를 요구받아 "완료자"로
        // 판정되지 않고 인트로 팝업이 재노출된다.
        // 빈 배열(flag 미대상/익명) → false. 아래 effect 가 이 값으로
        // hasShownTutorialIntroPopup 을 영구 마킹하므로 오판정이 치명적이다.
        return allMainMissionsCompletedIn(tutorialProgress.missions);
    }, [tutorialProgress]);
    // 메인 미션 모두 완료자 = NUX 인트로 팝업 노출 대상이 아니므로 atom 을 미리 마킹.
    // (앱 삭제/재설치 후 AsyncStorage 리셋 시 다시 노출되지 않도록.)
    useEffect(() => {
        if (allMainTutorialMissionsCompleted === true &&
            !hasShownTutorialIntroPopup) {
            setHasShownTutorialIntroPopup();
        }
    }, [
        allMainTutorialMissionsCompleted,
        hasShownTutorialIntroPopup,
        setHasShownTutorialIntroPopup,
    ]);
    // 1초 후 TutorialIntroPopup 노출 자격 부여 (홈 데이터 로딩 후).
    // 자격 부여만 — 실제 노출 여부는 orchestrator 가 직렬화 + 다른 조건과 함께 판단.
    useEffect(() => {
        if (__DEV__ && __DEV_FORCE_INTRO_POPUP__) {
            const timer = setTimeout(() => setTutorialIntroPopupReady(true), 500);
            return () => clearTimeout(timer);
        }
        const timer = setTimeout(() => setTutorialIntroPopupReady(true), 1000);
        return () => clearTimeout(timer);
    }, [__DEV_FORCE_INTRO_POPUP__]);
    // 홈 팝업 상태 (어드민에서 등록한 전면 팝업).
    const [dismissedPopupIds, setDismissedPopupIds] = useAtom(dismissedHomePopupIdsAtom);
    const activePopup = useMemo(() => {
        var _a, _b;
        const popups = (_a = homeData === null || homeData === void 0 ? void 0 : homeData.homePopups) !== null && _a !== void 0 ? _a : [];
        return (_b = popups.find(p => !dismissedPopupIds[p.id])) !== null && _b !== void 0 ? _b : null;
    }, [homeData === null || homeData === void 0 ? void 0 : homeData.homePopups, dismissedPopupIds]);
    useEffect(() => {
        if (activePopup) {
            prefetchRemoteImage(activePopup.imageUrl);
        }
    }, [activePopup === null || activePopup === void 0 ? void 0 : activePopup.imageUrl]);
    // === 홈 화면 오버레이 직렬화 (orchestrator) ===
    // 한 번에 하나의 오버레이만 노출. closedOverlayIds 는 현재 세션에서 닫힘 추적.
    //
    // 각 오버레이는 세 가지 status 중 하나:
    //  - 'eligible': 지금 노출할 수 있음 (모든 조건 만족)
    //  - 'pending': 곧 eligible 될 가능성 있음 (timing/API 로딩 대기). 후순위 차단.
    //  - 'ineligible': 노출 대상 아님 (atom 영구 dismiss / 결격 사유)
    //
    // activeOverlay 결정: 우선순위 순으로 순회하다가
    //  - closed 된 건 skip
    //  - pending 만나면 stop (후순위 차단 — 더 우선순위 높은 게 곧 뜰 예정)
    //  - eligible 만나면 그 오버레이를 노출
    //  - ineligible 은 skip 후 다음 후보로
    //
    // pending 이 없었으면 admin-home-popup 이 먼저 떴다가 3초 후 place-search-tutorial 이
    // 뜨면서 깜빡이는 UX 가 됨. pending 으로 차단해서 결정적인 순서 보장.
    const [closedOverlayIds, setClosedOverlayIds] = useState(new Set());
    // overlay 렌더 gate. 팝업이 navigation 을 트리거해서 closed → next overlay 가 activeOverlay 로
    // 바뀌어도 화면이 blur 된 상태에서는 Modal mount 자체를 막아 다른 페이지에 누수되지 않게.
    const isFocused = useIsFocused();
    const tutorialIntroStatus = (() => {
        if (__DEV__ && __DEV_FORCE_INTRO_POPUP__) {
            return tutorialIntroPopupReady ? 'eligible' : 'pending';
        }
        if (Platform.OS === 'web')
            return 'ineligible'; // 웹에선 윌리의 외출 인트로 팝업 미노출
        if (isAnonymousUser)
            return 'ineligible';
        if (featureFlags !== null &&
            !featureFlags.enabledFlags.has('USER_TUTORIAL'))
            return 'ineligible';
        if (hasShownTutorialIntroPopup)
            return 'ineligible';
        if (getDeferredDeepLinkUrl())
            return 'ineligible';
        if (allMainTutorialMissionsCompleted === true)
            return 'ineligible';
        // 결격 사유 없음 — 로딩/대기 중이면 pending, 다 끝났으면 eligible.
        if (featureFlags === null)
            return 'pending';
        if (allMainTutorialMissionsCompleted === undefined)
            return 'pending';
        if (!tutorialIntroPopupReady)
            return 'pending';
        return 'eligible';
    })();
    const adminHomePopupStatus = (() => {
        if (homeData === undefined)
            return 'pending'; // API 응답 대기 중
        return activePopup != null ? 'eligible' : 'ineligible';
    })();
    const marketingPushAgreementStatus = (() => {
        if (!isFeatureEnabled('MARKETING_PUSH_AGREEMENT'))
            return 'ineligible';
        if (homeData === undefined)
            return 'pending'; // API 응답 대기 중
        return homeData.needsMarketingPushAgreementConfirmation
            ? 'eligible'
            : 'ineligible';
    })();
    const overlays = [
        {
            id: 'tutorial-intro',
            status: tutorialIntroStatus,
            render: onClose => (_jsx(TutorialIntroPopup, { isVisible: true, onClose: () => {
                    setHasShownTutorialIntroPopup();
                    onClose();
                } })),
        },
        {
            id: 'admin-home-popup',
            status: adminHomePopupStatus,
            render: onClose => (_jsx(HomePopupModal, { popup: activePopup, visible: true, onClose: onClose, onImageClick: () => {
                    if (activePopup.clickUrl) {
                        const url = activePopup.clickUrl;
                        if (isAppDeepLink(url)) {
                            openAppDeepLink(url, navigation);
                        }
                        else {
                            navigation.navigate('Webview', { url });
                        }
                    }
                    onClose();
                }, onDismissPermanently: () => {
                    setDismissedPopupIds(prev => (Object.assign(Object.assign({}, prev), { [activePopup.id]: true })));
                    onClose();
                } })),
        },
        {
            id: 'marketing-push-agreement',
            status: marketingPushAgreementStatus,
            render: onClose => (_jsx(MarketingPushAgreementBottomSheet, { isVisible: true, onClose: onClose })),
        },
    ];
    const activeOverlay = (() => {
        for (const o of overlays) {
            if (closedOverlayIds.has(o.id))
                continue;
            if (o.status === 'pending')
                return undefined; // 후순위 차단
            if (o.status === 'eligible')
                return o;
            // ineligible 은 skip
        }
        return undefined;
    })();
    const handleActiveOverlayClose = useCallback(() => {
        if (!activeOverlay)
            return;
        setClosedOverlayIds(prev => {
            const next = new Set(prev);
            next.add(activeOverlay.id);
            return next;
        });
    }, [activeOverlay]);
    useEffect(() => {
        const requestGeolocationPermissionIfNeeded = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const location = yield GeolocationUtils.getCurrentPosition();
                setGeolocationErrorReason(null);
                setCurrentLocation({
                    latitude: location.coords.latitude,
                    longitude: location.coords.longitude,
                });
            }
            catch (error) {
                Logger.logError(error);
                if (error.code === 1) {
                    setGeolocationErrorReason('permission_denied');
                }
                else if (error.code === 2) {
                    setGeolocationErrorReason('location_unavailable');
                }
            }
        });
        requestGeolocationPermissionIfNeeded();
        // deps 는 accessToken 하나다. navigation·syncUserInfo 는 이 effect 가 쓰지도 않는데
        // syncUserInfo 가 렌더마다 새로 만들어지는 함수라, 넣어두면
        // effect 실행 → setCurrentLocation → atom 구독으로 리렌더 → effect 재실행 의
        // 무한 루프가 된다(실측: 초당 4회 getCurrentPosition + 지도 마커 361개 재생성).
    }, [accessToken]);
    useEffect(() => {
        syncUserInfo();
    }, []);
    useEffect(() => {
        const requestIOSUserPermission = () => __awaiter(void 0, void 0, void 0, function* () {
            const authStatus = yield getMessaging().requestPermission();
            const enabled = authStatus === AuthorizationStatus.AUTHORIZED ||
                authStatus === AuthorizationStatus.PROVISIONAL;
            if (enabled) {
                console.log('Authorization status:', authStatus);
            }
        });
        if (Platform.OS === 'android') {
            PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS);
        }
        else {
            requestIOSUserPermission();
        }
    }, []);
    useEffect(() => {
        if (!isAnonymousUser) {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const pushToken = yield getMessaging().getToken();
                yield api.updatePushTokenPost({ pushToken });
            }))();
        }
        return getMessaging().onTokenRefresh(pushToken => {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                yield api.updatePushTokenPost({ pushToken });
            }))();
        });
    }, [isAnonymousUser]);
    const openStore = () => {
        const appStoreUrl = 'https://apps.apple.com/kr/app/계단뿌셔클럽/id6444382843';
        const playStoreUrl = 'https://play.google.com/store/apps/details?id=club.staircrusher';
        const url = Platform.OS === 'ios' ? appStoreUrl : playStoreUrl;
        Linking.openURL(url).catch(err => console.error('An error occurred', err));
    };
    const lastBackPressTime = useRef(0);
    const handleBackPress = useCallback(() => {
        if (lastBackPressTime.current + 2000 >= Date.now()) {
            return false;
        }
        else {
            ToastUtils.show('뒤로가기 버튼을 한번 더 누르면 종료됩니다.');
            lastBackPressTime.current = Date.now();
            return true;
        }
    }, []);
    useFocusEffect(useCallback(() => {
        const subscription = BackHandler.addEventListener('hardwareBackPress', handleBackPress);
        return () => {
            subscription.remove();
        };
    }, []));
    const [isScrollEnabled, setIsScrollEnabled] = useState(true);
    const handleBannerPanStateChange = useCallback((isPanning) => {
        setIsScrollEnabled(!isPanning);
    }, []);
    // WhiteCard가 safe area 상단에 닿으면 흰색 오버레이 표시
    const [showSafeAreaOverlay, setShowSafeAreaOverlay] = useState(false);
    const whiteCardYRef = useRef(0);
    const handleWhiteCardLayout = useCallback((e) => {
        whiteCardYRef.current = e.nativeEvent.layout.y;
    }, []);
    const handleScroll = useCallback((e) => {
        const scrollY = e.nativeEvent.contentOffset.y;
        // WhiteCard 상단이 safe area 아래로 들어오는 시점
        const threshold = whiteCardYRef.current - insets.top;
        const shouldShow = scrollY >= threshold;
        setShowSafeAreaOverlay(shouldShow);
    }, [insets.top]);
    const queryClient = useQueryClient();
    useEffect(() => {
        // Prefetch search presets
        function prefetchListSearchPlacePresets() {
            queryClient.prefetchQuery({
                queryKey: ['ListSearchPlacePresets'],
                queryFn: () => __awaiter(this, void 0, void 0, function* () { var _a, _b; return (_b = (_a = (yield api.listSearchPlacePresetsPost({}))) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.keywordPresets; }),
            });
        }
        // Prefetch nearby accessibility status
        // 응답 전체를 한 캐시에 담는다 — 소비처(검색 추천)가 select 로 필요한 필드를 뽑는다.
        // 예전엔 여기서 conqueredCount 만 저장하고 소비처마다 키가 달라 같은 엔드포인트를
        // 여러 번 호출했다.
        function prefetchNearbyAccessibilityStatus() {
            queryClient.prefetchQuery({
                queryKey: nearbyAccessibilityStatusQueryKey(userInfo === null || userInfo === void 0 ? void 0 : userInfo.id),
                queryFn: nearbyAccessibilityStatusQueryFn(api),
            });
        }
        prefetchListSearchPlacePresets();
        prefetchNearbyAccessibilityStatus();
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx(ScreenLayout, { isHeaderVisible: false, style: { backgroundColor: color.white }, children: _jsxs(Container, { children: [_jsx(BackgroundImage, { source: require('@/assets/img/home_screen_bg.png') }), _jsxs(ScrollView, { bounces: false, showsVerticalScrollIndicator: false, scrollEnabled: isScrollEnabled, onScroll: handleScroll, scrollEventThrottle: 16, children: [_jsxs(InnerContainer, { style: { paddingTop: insets.top }, children: [_jsx(LogoContainer, { children: _jsx(CrusherClubLogo, {}) }), _jsx(SearchButtonSection, {}), _jsx(CategoryChipSection, {})] }), _jsxs(WhiteCard, { onLayout: handleWhiteCardLayout, children: [_jsx(QuickMenuSection, { challenge: (_b = homeData === null || homeData === void 0 ? void 0 : homeData.quickAction) === null || _b === void 0 ? void 0 : _b.challenge }), _jsx(ArticleSection, {}), _jsx(MainBannerSection, { banners: (_c = homeData === null || homeData === void 0 ? void 0 : homeData.mainBanners) !== null && _c !== void 0 ? _c : [], onPanStateChange: handleBannerPanStateChange, isLoading: isHomeDataLoading }), _jsx(RecommendedContentSection, { contents: (_d = homeData === null || homeData === void 0 ? void 0 : homeData.recommendedContents) !== null && _d !== void 0 ? _d : [], isLoading: isHomeDataLoading }), _jsx(StripBannerSection, { banners: (_e = homeData === null || homeData === void 0 ? void 0 : homeData.stripBanners) !== null && _e !== void 0 ? _e : [], onPanStateChange: handleBannerPanStateChange, isLoading: isHomeDataLoading })] }), _jsx(FooterButtonsSection, { isLoading: isHomeDataLoading })] }), showSafeAreaOverlay && (_jsx(SafeAreaOverlay, { style: { height: insets.top } })), _jsx(GeolocationPermissionBottomSheet, { isVisible: geolocationErrorReason !== null, errorReason: geolocationErrorReason !== null && geolocationErrorReason !== void 0 ? geolocationErrorReason : 'permission_denied' }), (versionStatus ===
                            GetClientVersionStatusResponseDtoStatusEnum.UpgradeNeeded ||
                            versionStatus ===
                                GetClientVersionStatusResponseDtoStatusEnum.UpgradeRecommended) && (_jsx(AppUpgradeNeededBottomSheet, { isVisible: showAppUpgradeNeeded, isRequired: versionStatus ===
                                GetClientVersionStatusResponseDtoStatusEnum.UpgradeNeeded, message: versionStatusMessage !== null && versionStatusMessage !== void 0 ? versionStatusMessage : '', onConfirmButtonPressed: openStore, onCloseButtonPressed: () => {
                                setShowAppUpgradeNeeded(false);
                            } }))] }) }), isFocused && (activeOverlay === null || activeOverlay === void 0 ? void 0 : activeOverlay.render(handleActiveOverlayClose))] }));
};
export default HomeScreenV2;
const Container = styled.View `
  flex: 1;
  background-color: ${color.gray15};
`;
const LogoContainer = styled.View `
  align-items: center;
  justify-content: center;
  padding-top: 32px;
  padding-bottom: 28px;
  margin-bottom: 4px;
`;
const BackgroundImage = styled(Image) `
  position: absolute;
  top: 0;
  left: 0;
  width: ${SCREEN_WIDTH}px;
  height: ${BG_HEIGHT}px;
`;
const SafeAreaOverlay = styled.View `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${color.white};
`;
const InnerContainer = styled.View ``;
const WhiteCard = styled.View `
  background-color: ${color.white};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  shadow-color: #000;
  shadow-offset: 0px -4px;
  shadow-opacity: 0.12;
  shadow-radius: 16px;
  elevation: 8;
  margin-top: 25px;
  padding-bottom: 40px;
`;
